/**
 * Type-safe HTTP client with retry logic and offline queue
 *
 * Now integrated with Supabase Auth:
 * - Automatically adds Supabase auth token to requests
 * - Handles 401 errors by attempting token refresh
 * - Falls back to offline queue on network errors
 */

import ky, { type KyInstance } from 'ky';
import { queueOfflineAction } from '../db/sync';
import { getAuthToken } from '../lib/auth';

/**
 * Create API client instance
 */
const createApiClient = (): KyInstance => {
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';

  console.log('[API] Creating client with URL:', apiUrl);

  return ky.create({
    prefixUrl: apiUrl,
    timeout: 30000, // Increased timeout for slow connections
    // Note: Don't use credentials:'include' as we use Bearer tokens, not cookies
    // credentials: 'include' can cause preflight issues
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
    retry: {
      limit: 2,
      methods: ['get', 'post'],
      statusCodes: [408, 413, 429, 500, 502, 503, 504],
      backoffLimit: 3000
    },
    hooks: {
      beforeRequest: [
        async (request) => {
          console.log('[API] beforeRequest - URL:', request.url);

          // Add timestamp for request tracking
          request.headers.set('X-Request-Time', Date.now().toString());

          // Add device ID if available
          const deviceId = localStorage.getItem('deviceId');
          if (deviceId) {
            request.headers.set('X-Device-Id', deviceId);
          }

          // Add Supabase auth token if available
          try {
            const token = await getAuthToken();
            console.log('[API] Auth token available:', !!token);
            if (token) {
              request.headers.set('Authorization', `Bearer ${token}`);
            } else {
              console.warn('[API] No auth token available for request');
            }
          } catch (error) {
            console.error('[API] Failed to get auth token:', error);
          }
        }
      ],
      afterResponse: [
        async (_request, _options, response) => {
          if (response.ok) {
            return response;
          }

          // Handle specific error codes
          if (response.status === 409) {
            // Conflict detected - will be handled by caller
            return response;
          }

          return response;
        }
      ],
      beforeError: [
        async (error) => {
          console.error('[API] Request error:', {
            name: error.name,
            message: error.message,
            url: error.request?.url,
            status: error.response?.status
          });

          const { request } = error;

          // Queue for offline sync if network error
          if (error.name === 'TimeoutError' || !navigator.onLine) {
            console.log('[API] Queueing for offline sync due to:', error.name);
            try {
              const body = await request.clone().text();
              await queueOfflineAction({
                url: request.url,
                method: request.method,
                body,
                headers: Object.fromEntries(request.headers.entries())
              });
            } catch (queueError) {
              console.error('[API] Failed to queue offline action:', queueError);
            }
          }

          return error;
        }
      ]
    }
  });
};

/**
 * API client singleton
 */
export const api = createApiClient();

/**
 * Check if client is online
 */
export const isOnline = (): boolean => {
  return navigator.onLine;
};

/**
 * Network status change event
 */
export const onNetworkStatusChange = (callback: (online: boolean) => void): (() => void) => {
  const handleOnline = () => callback(true);
  const handleOffline = () => callback(false);

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Return cleanup function
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
};
